/**
 * _field_write.js: write a form field the way a person's own edit would.
 *
 * The one owner of scripted writes. Chip AI form fill (chip_fill_controller.js)
 * writes a value at once; the first-run walkthrough (_walkthrough_fill.js)
 * types one character at a time. Either way the controllers that watch a
 * field (client-mode, client-match, form-guard, the description editor) see
 * what a person's edit fires: `input` for each change of text, then one
 * `change` once the value is in.
 *
 * Three kinds of field, told apart by what the element carries:
 *   an address input (input.autocompleteInstance, address_autocomplete.js)
 *     is written through its setText(), which fires input and change itself
 *     and never searches;
 *   the description editor (element.chippieSetText,
 *     rich_description_controller.js) has its prose replaced and its photos
 *     kept, and fires `input` on its own hidden textarea once mirrored;
 *   any other input has its value set, then the events.
 *
 * Nothing here focuses a field, so a phone raises no keyboard. A stopped run
 * (an aborted AbortSignal) rejects with the signal's reason, an AbortError
 * when it was aborted without one, and leaves no timer running.
 */

/**
 * Write `value` into `el` at once, as a paste would. Returns a function that
 * puts the field back.
 */
export function writeText(el, value) {
    // The rich description editor: Chip writes its words and leaves its
    // photos (rich_description_controller.js), and undo restores the markup.
    if (el.chippieSetText) {
        const before = el.chippieGetHTML();
        el.chippieSetText(String(value));
        return () => { el.chippieSetHTML(before); announce(el); };
    }
    const prop = el.isContentEditable ? "innerHTML" : "value";
    const before = el[prop];
    if (el.isContentEditable) el.textContent = String(value);
    else el.value = String(value);
    announce(el);
    return () => { el[prop] = before; announce(el); };
}

/**
 * Type `text` into `el`, one character every `keystroke` ms, as the field's
 * whole content. With no keystroke the whole value is written at once.
 * Resolves once the last character is in.
 */
export async function typeText(el, text, { keystroke = 0, signal } = {}) {
    signal?.throwIfAborted();
    const value = String(text);
    if (keystroke > 0) {
        for (let length = 1; length < value.length; length++) {
            await wait(keystroke, signal);
            put(el, value.slice(0, length), false);
        }
        await wait(keystroke, signal);
    }
    put(el, value, true);
}

/** Resolves after `ms`; a stop rejects at once and clears the timer. */
export function wait(ms, signal) {
    if (signal?.aborted) return Promise.reject(signal.reason);
    if (!(ms > 0)) return Promise.resolve();
    return new Promise((resolve, reject) => {
        const stop = () => {
            clearTimeout(timer);
            reject(signal.reason);
        };
        const timer = setTimeout(() => {
            signal?.removeEventListener("abort", stop);
            resolve();
        }, ms);
        signal?.addEventListener("abort", stop, { once: true });
    });
}

export function check(radio) {
    radio.checked = true;
    radio.dispatchEvent(new Event("change", { bubbles: true }));
}

export function uncheck(radio) {
    radio.checked = false;
    radio.dispatchEvent(new Event("change", { bubbles: true }));
}

// One keystroke's worth of `text`; `last` adds the field's one `change`.
function put(el, text, last) {
    if (el.autocompleteInstance) el.autocompleteInstance.setText(text);
    else if (el.chippieSetText) el.chippieSetText(text);
    else {
        el.value = text;
        el.dispatchEvent(new Event("input", { bubbles: true }));
        if (last) el.dispatchEvent(new Event("change", { bubbles: true }));
    }
}

function announce(el) {
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.dispatchEvent(new Event("change", { bubbles: true }));
}
